import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, CheckCircle, XCircle, Store as StoreIconLucide, Globe, Key, Lock, Tag, Shield, Plug, Upload, ShoppingBag } from 'lucide-react';
import { storeService, CreateStoreRequest, Store, TestConnectionResponse, ConnectorType } from '../../services/store';

interface StoreFormProps {
  store?: Store;
  onSuccess: (store: Store) => void;
  onCancel?: () => void;
}

export function StoreForm({ store, onSuccess, onCancel }: StoreFormProps) {
  const [connectorType, setConnectorType] = useState<ConnectorType>(store?.connector_type || 'magento');
  const [formData, setFormData] = useState({
    name: store?.name || '',
    url: store?.url || '',
    consumer_key: store?.consumer_key || '',
    consumer_secret: '',
    access_token: store?.access_token || '',
    access_token_secret: '',
    magento_version: (store?.magento_version as '2.3' | '2.4') || '2.4',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestConnectionResponse | null>(null);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Connector name is required';
    }

    // Only validate Magento fields if connector_type is 'magento'
    if (connectorType === 'magento') {
      if (!formData.url.trim()) {
        newErrors.url = 'Store URL is required';
      } else if (!/^https?:\/\/.+/.test(formData.url)) {
        newErrors.url = 'Please enter a valid URL (including http:// or https://)';
      }

      if (!formData.consumer_key.trim()) {
        newErrors.consumer_key = 'Consumer key is required';
      }

      if (!formData.consumer_secret.trim() && !store) {
        newErrors.consumer_secret = 'Consumer secret is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    setTestResult(null);
  };

  const handleTestConnection = async () => {
    if (!validateForm()) return;

    setIsTesting(true);
    setTestResult(null);

    try {
      const result = await storeService.testConnection(formData as CreateStoreRequest);
      setTestResult(result);
    } catch (error: any) {
      setTestResult({
        success: false,
        message: error.message || 'Failed to test connection',
      });
    } finally {
      setIsTesting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      let result: Store;

      if (store) {
        const updateData: any = {
          name: formData.name,
          connector_type: connectorType,
        };

        // Only include Magento fields for Magento connectors
        if (connectorType === 'magento') {
          updateData.url = formData.url;
          updateData.consumer_key = formData.consumer_key;
          updateData.access_token = formData.access_token;
          updateData.magento_version = formData.magento_version;

          if (formData.consumer_secret) {
            updateData.consumer_secret = formData.consumer_secret;
          }
          if (formData.access_token_secret) {
            updateData.access_token_secret = formData.access_token_secret;
          }
        }

        result = await storeService.updateStore(store.id, updateData);
      } else {
        const createData: CreateStoreRequest = {
          name: formData.name,
          connector_type: connectorType,
        };

        // Only include Magento fields for Magento connectors
        if (connectorType === 'magento') {
          createData.url = formData.url;
          createData.consumer_key = formData.consumer_key;
          createData.consumer_secret = formData.consumer_secret;
          createData.access_token = formData.access_token;
          createData.access_token_secret = formData.access_token_secret;
          createData.magento_version = formData.magento_version;
        }

        result = await storeService.createStore(createData);
      }

      onSuccess(result);
    } catch (error: any) {
      setErrors({ submit: error.message || 'Failed to save connector' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto animate-fade-in">
      <Card className="form-enhanced">
        <CardHeader className="pb-6">
          <div className="flex items-center gap-3 animate-slide-down">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Plug className="w-6 h-6 text-primary" />
            </div>
            <div>
              <CardTitle className="text-2xl font-bold">{store ? 'Edit Connector' : 'Add New Connector'}</CardTitle>
              <CardDescription className="text-base mt-1">
                {store ? 'Update your connector settings' : 'Connect a Magento store or create an upload connector'}
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Connector Type Selection - Only show when creating new */}
          {!store && (
            <div className="space-y-4 animate-slide-up">
              <h3 className="text-lg font-semibold text-foreground border-b border-border pb-2">Choose Connector Type</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setConnectorType('magento')}
                  className={`p-6 rounded-xl border-2 transition-all duration-300 text-left ${
                    connectorType === 'magento'
                      ? 'border-primary bg-primary/10 shadow-lg'
                      : 'border-border hover:border-primary/50 hover:bg-muted/50'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-lg ${connectorType === 'magento' ? 'bg-primary/20' : 'bg-muted'}`}>
                      <ShoppingBag className={`w-6 h-6 ${connectorType === 'magento' ? 'text-primary' : 'text-muted-foreground'}`} />
                    </div>
                    <div>
                      <h4 className={`font-semibold text-lg ${connectorType === 'magento' ? 'text-primary' : 'text-foreground'}`}>
                        Magento Store
                      </h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        Connect to a Magento 2 store to sync and process product images automatically
                      </p>
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setConnectorType('upload')}
                  className={`p-6 rounded-xl border-2 transition-all duration-300 text-left ${
                    connectorType === 'upload'
                      ? 'border-primary bg-primary/10 shadow-lg'
                      : 'border-border hover:border-primary/50 hover:bg-muted/50'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-lg ${connectorType === 'upload' ? 'bg-primary/20' : 'bg-muted'}`}>
                      <Upload className={`w-6 h-6 ${connectorType === 'upload' ? 'text-primary' : 'text-muted-foreground'}`} />
                    </div>
                    <div>
                      <h4 className={`font-semibold text-lg ${connectorType === 'upload' ? 'text-primary' : 'text-foreground'}`}>
                        Upload Connector
                      </h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        Upload images directly, process them, and download the results
                      </p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* OAuth Setup Instructions - Only show for Magento when creating new */}
          {!store && connectorType === 'magento' && (
            <div className="p-6 bg-gradient-to-r from-primary/10 to-primary/15 border border-primary/20 rounded-xl animate-slide-up">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-primary/20 rounded-lg flex-shrink-0">
                  <Shield className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-semibold text-primary text-lg mb-3">OAuth Setup Instructions</h4>
                  <p className="text-primary/90 mb-4">
                    To connect your Magento 2 store, you'll need to create OAuth credentials in your Magento admin panel:
                  </p>
                  <ol className="text-sm text-primary/90 space-y-2 pl-4">
                    <li className="flex items-start gap-2">
                      <span className="inline-block w-6 h-6 bg-primary/20 text-primary rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                      Go to <strong>System → Integrations</strong> in your Magento admin
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="inline-block w-6 h-6 bg-primary/20 text-primary rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
                      Click <strong>"Add New Integration"</strong>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="inline-block w-6 h-6 bg-primary/20 text-primary rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
                      Fill in the integration details and select API resources
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="inline-block w-6 h-6 bg-primary/20 text-primary rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">4</span>
                      Save and activate the integration
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="inline-block w-6 h-6 bg-primary/20 text-primary rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">5</span>
                      Copy the <strong>Consumer Key</strong> and <strong>Consumer Secret</strong> to the form below
                    </li>
                  </ol>
                </div>
              </div>
            </div>
          )}

          {/* Upload Connector Info */}
          {!store && connectorType === 'upload' && (
            <div className="p-6 bg-gradient-to-r from-green-500/10 to-emerald-500/15 border border-green-500/20 rounded-xl animate-slide-up">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-green-500/20 rounded-lg flex-shrink-0">
                  <Upload className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-green-700 dark:text-green-400 text-lg mb-3">Upload Connector</h4>
                  <p className="text-green-700/90 dark:text-green-400/90 mb-4">
                    With an upload connector, you can:
                  </p>
                  <ul className="text-sm text-green-700/90 dark:text-green-400/90 space-y-2">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Upload 10-500 images at once
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Apply AI transformations (background removal, upscaling, etc.)
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Download processed images individually or as ZIP
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        
          <form onSubmit={handleSubmit} className="space-y-6 animate-slide-up">
            {/* Basic Information */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-foreground border-b border-border pb-2">Basic Information</h3>

              <div className={connectorType === 'upload' ? '' : 'grid grid-cols-1 md:grid-cols-2 gap-6'}>
                <div className="space-y-2 group">
                  <label htmlFor="name" className="text-sm font-medium text-foreground flex items-center gap-2">
                    <Plug className="w-4 h-4 text-primary" />
                    Connector Name
                  </label>
                  <Input
                    id="name"
                    type="text"
                    placeholder={connectorType === 'magento' ? 'My Magento Store' : 'My Upload Connector'}
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className={`input-enhanced transition-all duration-300 ${errors.name ? 'border-destructive animate-bounce' : 'group-hover:border-primary/50'}`}
                  />
                  {errors.name && (
                    <p className="text-sm text-destructive animate-slide-down flex items-center gap-1">
                      <XCircle className="w-3 h-3" />
                      {errors.name}
                    </p>
                  )}
                </div>

                {/* Store URL - Only for Magento */}
                {connectorType === 'magento' && (
                  <div className="space-y-2 group">
                    <label htmlFor="url" className="text-sm font-medium text-foreground flex items-center gap-2">
                      <Globe className="w-4 h-4 text-primary" />
                      Store URL
                    </label>
                    <Input
                      id="url"
                      type="url"
                      placeholder="https://mystore.com"
                      value={formData.url}
                      onChange={(e) => handleInputChange('url', e.target.value)}
                      className={`input-enhanced transition-all duration-300 ${errors.url ? 'border-destructive animate-bounce' : 'group-hover:border-primary/50'}`}
                    />
                    {errors.url && (
                      <p className="text-sm text-destructive animate-slide-down flex items-center gap-1">
                        <XCircle className="w-3 h-3" />
                        {errors.url}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* OAuth Credentials - Only for Magento */}
            {connectorType === 'magento' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-foreground border-b border-border pb-2">OAuth Credentials</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2 group">
                    <label htmlFor="consumer_key" className="text-sm font-medium text-foreground flex items-center gap-2">
                      <Key className="w-4 h-4 text-primary" />
                      Consumer Key
                    </label>
                    <Input
                      id="consumer_key"
                      type="text"
                      placeholder="Enter your Magento OAuth consumer key"
                      value={formData.consumer_key}
                      onChange={(e) => handleInputChange('consumer_key', e.target.value)}
                      className={`input-enhanced font-mono transition-all duration-300 ${errors.consumer_key ? 'border-destructive animate-bounce' : 'group-hover:border-primary/50'}`}
                    />
                    {errors.consumer_key && (
                      <p className="text-sm text-destructive animate-slide-down flex items-center gap-1">
                        <XCircle className="w-3 h-3" />
                        {errors.consumer_key}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2 group">
                    <label htmlFor="consumer_secret" className="text-sm font-medium text-foreground flex items-center gap-2">
                      <Lock className="w-4 h-4 text-primary" />
                      Consumer Secret
                      {store && <span className="text-muted-foreground text-xs">(leave blank to keep current)</span>}
                    </label>
                    <Input
                      id="consumer_secret"
                      type="password"
                      placeholder="Enter your Magento OAuth consumer secret"
                      value={formData.consumer_secret}
                      onChange={(e) => handleInputChange('consumer_secret', e.target.value)}
                      className={`input-enhanced font-mono transition-all duration-300 ${errors.consumer_secret ? 'border-destructive animate-bounce' : 'group-hover:border-primary/50'}`}
                    />
                    {errors.consumer_secret && (
                      <p className="text-sm text-destructive animate-slide-down flex items-center gap-1">
                        <XCircle className="w-3 h-3" />
                        {errors.consumer_secret}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Advanced Settings - Only for Magento */}
            {connectorType === 'magento' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-foreground border-b border-border pb-2">Advanced Settings</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2 group">
                    <label htmlFor="access_token" className="text-sm font-medium text-foreground flex items-center gap-2">
                      <Key className="w-4 h-4 text-muted-foreground" />
                      Access Token
                      <span className="text-muted-foreground text-xs">(optional)</span>
                    </label>
                    <Input
                      id="access_token"
                      type="text"
                      placeholder="Enter access token if available"
                      value={formData.access_token}
                      onChange={(e) => handleInputChange('access_token', e.target.value)}
                      className="input-enhanced font-mono transition-all duration-300 group-hover:border-primary/50"
                    />
                  </div>

                  <div className="space-y-2 group">
                    <label htmlFor="access_token_secret" className="text-sm font-medium text-foreground flex items-center gap-2">
                      <Lock className="w-4 h-4 text-muted-foreground" />
                      Access Token Secret
                      <span className="text-muted-foreground text-xs">(optional)</span>
                    </label>
                    <Input
                      id="access_token_secret"
                      type="password"
                      placeholder="Enter access token secret if available"
                      value={formData.access_token_secret}
                      onChange={(e) => handleInputChange('access_token_secret', e.target.value)}
                      className="input-enhanced font-mono transition-all duration-300 group-hover:border-primary/50"
                    />
                  </div>

                  <div className="space-y-2 group">
                    <label htmlFor="magento_version" className="text-sm font-medium text-foreground flex items-center gap-2">
                      <Tag className="w-4 h-4 text-primary" />
                      Magento Version
                    </label>
                    <Select
                      value={formData.magento_version}
                      onValueChange={(value) => handleInputChange('magento_version', value)}
                    >
                      <SelectTrigger className="input-enhanced">
                        <SelectValue placeholder="Select Magento version" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="2.4">Magento 2.4 (Recommended)</SelectItem>
                        <SelectItem value="2.3">Magento 2.3</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* Test Results */}
            {testResult && (
              <div className="animate-fade-in">
                <Alert variant={testResult.success ? "success" : "destructive"} className="shadow-lg">
                  <div className="flex items-start space-x-3">
                    {testResult.success ? (
                      <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
                    ) : (
                      <XCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <AlertDescription className="text-sm font-medium">
                        {testResult.message}
                        {testResult.store_info && (
                          <div className="mt-3 p-3 bg-card rounded-lg border border-border">
                            <h4 className="font-semibold mb-2">Store Information</h4>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                              <div><strong>Name:</strong> {testResult.store_info.name}</div>
                              <div><strong>Version:</strong> {testResult.store_info.version}</div>
                              <div><strong>Currency:</strong> {testResult.store_info.base_currency}</div>
                            </div>
                          </div>
                        )}
                      </AlertDescription>
                    </div>
                  </div>
                </Alert>
              </div>
            )}

            {errors.submit && (
              <Alert className="border-destructive/20 bg-destructive/10 animate-bounce-in">
                <XCircle className="h-5 w-5 text-destructive" />
                <AlertDescription className="text-destructive font-medium">{errors.submit}</AlertDescription>
              </Alert>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-border">
              {/* Test Connection - Only for Magento */}
              {connectorType === 'magento' && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleTestConnection}
                  disabled={isTesting || !formData.name || !formData.url || !formData.consumer_key || (!formData.consumer_secret && !store)}
                  className="flex-1 hover:bg-primary/10 hover:border-primary/30 hover:text-primary transition-all duration-300"
                >
                  {isTesting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Testing Connection...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Test Connection
                    </>
                  )}
                </Button>
              )}

              <Button
                type="submit"
                disabled={isSubmitting || !formData.name}
                className="flex-1 bg-gradient-primary hover:shadow-lg hover:scale-105 transition-all duration-300"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {store ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-2 h-4 w-4" />
                    {store ? 'Update Connector' : 'Create Connector'}
                  </>
                )}
              </Button>

              {onCancel && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  className="hover:bg-muted transition-colors duration-200"
                >
                  Cancel
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}